import { Col } from "../Layout/Grid";
import { PersonOutline as UserOutlined } from "@mui/icons-material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Box from "@mui/material/Box";
import { useOptionalCurrentUser } from "../../../src/utils/hooks";
import React, { ReactNode } from "react";
import { ChatSpeaker } from "./types";
import { speakerTint } from "./speakerTint";
import { AVATAR_GAP, AVATAR_SIZE, SPEAKER_GAP } from "./chatLayout";

/** Every speaker's avatar: one size and shape, so the three read as a set. */
function SpeakerAvatar({ children, accent }: { children: ReactNode; accent?: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: accent ?? "action.hover",
        color: accent ? "#fff" : "text.primary",
        fontSize: 13,
        fontWeight: 700,
        "& svg": { fontSize: 16 },
      }}
    >
      {children}
    </Box>
  );
}

const nameSx = {
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.4,
  color: "text.primary",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const ChatMessageHead = React.memo(
  function ChatMessageHead({
    type,
    separator,
    agentCharacter,
    speaker,
  }: {
    type: "agent" | "user";
    /** Not the first speaker block: space it from the one above. */
    separator?: boolean;
    agentCharacter: {
      name: string;
    };
    /** A responder other than the chat's own assistant (a persistent agent). */
    speaker?: ChatSpeaker;
  }) {
    // null until the current user is loaded into the Apollo cache
    const currentUser = useOptionalCurrentUser();
    const isUser = type === "user";
    const accent = speaker ? speakerTint(speaker.key, speaker.color).accent : undefined;

    const avatar = isUser ? (
      <SpeakerAvatar>
        <UserOutlined />
      </SpeakerAvatar>
    ) : speaker ? (
      <SpeakerAvatar accent={accent}>{speaker.name.charAt(0).toUpperCase()}</SpeakerAvatar>
    ) : (
      <SpeakerAvatar>
        <AutoAwesomeIcon />
      </SpeakerAvatar>
    );

    const name = isUser
      ? currentUser?.commonName || currentUser?.username || "You"
      : speaker?.name ?? agentCharacter.name;

    return (
      <Col span={24}>
        <Box
          data-testid={isUser ? "chat-head-user" : speaker ? "chat-head-speaker" : "chat-head-assistant"}
          // The user speaks from the right, mirrored; every responder from the left.
          data-align={isUser ? "right" : "left"}
          data-accent={accent}
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: isUser ? "row-reverse" : "row",
            gap: `${AVATAR_GAP}px`,
            mt: separator ? `${SPEAKER_GAP}px` : 0,
            mb: 0.5,
            minWidth: 0,
          }}
        >
          {avatar}
          <Box component="span" sx={nameSx}>
            {name}
          </Box>
          {speaker?.note && (
            <Box component="span" sx={{ fontSize: 12, color: "text.secondary", flexShrink: 0 }}>
              {speaker.note}
            </Box>
          )}
        </Box>
      </Col>
    );
  },
  (prevProps, nextProps) =>
    prevProps.type === nextProps.type &&
    prevProps.separator === nextProps.separator &&
    prevProps.agentCharacter === nextProps.agentCharacter &&
    prevProps.speaker?.key === nextProps.speaker?.key &&
    prevProps.speaker?.note === nextProps.speaker?.note &&
    prevProps.speaker?.color === nextProps.speaker?.color
);

export { ChatMessageHead };
